import type { PartialBlock } from "@blocknote/core";
import { inlineContentToText } from "@/lib/utils/blocknote-plain-text";

export type SummarySection = {
  title: string;
  bullets?: (string | { text: string })[];
  body?: string;
};

/** Section shape `updateEngagementSummary` accepts. */
export type EditedSummarySection = {
  title: string;
  bullets: string[];
};

/** Title used for the legacy (`engagement.aiSummary`) single-section save. */
export const DEFAULT_SUMMARY_SECTION_TITLE = "Meeting Summary";

const MEETING_SUMMARY_TITLE = /^meeting summary$/i;

// Convert summary bullet points to BlockNote blocks
export function summaryPointsToBlocks(points: string[]): PartialBlock[] {
  if (points.length === 0) return [{ type: "paragraph" }];
  const blocks: PartialBlock[] = [];
  points.forEach(point => {
    blocks.push({
      type: "bulletListItem",
      content: [{ type: "text" as const, text: point, styles: {} }],
    });
  });
  return blocks;
}

// Convert summary sections to BlockNote blocks
export function summaryToBlocks(summary: SummarySection[]): PartialBlock[] {
  const blocks: PartialBlock[] = [];

  summary.forEach((section, idx) => {
    if (!section || (!section.bullets && !section.body)) return;

    // Skip the first section title if it's "Meeting Summary" (already shown as header above)
    const skipTitle = idx === 0 && MEETING_SUMMARY_TITLE.test(section.title);

    if (!skipTitle) {
      // Add section title as heading
      blocks.push({
        type: "heading",
        props: { level: 3 },
        content: [{ type: "text" as const, text: String(section.title ?? ""), styles: {} }],
      });
    }

    // Add bullets as bullet list if present
    if (Array.isArray(section.bullets)) {
      section.bullets.forEach((bullet) => {
        const text = typeof bullet === 'string' ? bullet : String(bullet?.text ?? "");
        blocks.push({
          type: "bulletListItem",
          content: [{ type: "text" as const, text, styles: {} }],
        });
      });
    }

    // Add body as paragraph if present and no bullets
    if (section.body && !section.bullets) {
      blocks.push({
        type: "paragraph",
        content: [{ type: "text" as const, text: String(section.body ?? ""), styles: {} }],
      });
    }

    // Add spacing between sections
    blocks.push({ type: "paragraph" });
  });

  return blocks.length > 0 ? blocks : [{ type: "paragraph" }];
}

type BlockLike = {
  type?: string;
  content?: unknown;
  children?: BlockLike[];
};

/** Every non-empty text line of the document, depth-first (headings excluded unless asked). */
function collectLines(
  blocks: readonly BlockLike[],
  visit: (block: BlockLike, text: string, depth: number) => void,
  depth = 0,
) {
  for (const block of blocks) {
    if (!block) continue;
    visit(block, inlineContentToText(block.content).trim(), depth);
    if (Array.isArray(block.children) && block.children.length > 0) {
      collectLines(block.children, visit, depth + 1);
    }
  }
}

/**
 * Inverse of `summaryToBlocks` for a template summary
 * (`engagement.summaries[].summary.sections`).
 *
 * - A top-level heading starts a new section; an empty heading does not.
 * - Every other non-empty text block (bullets, paragraphs, nested children)
 *   becomes a bullet string — the save contract is `{title, bullets}`, so a
 *   body-only section comes back as a one-bullet section.
 * - Content before the first heading belongs to the first section whose title
 *   `summaryToBlocks` hid ("Meeting Summary"): it reuses the original first
 *   section's title when that one was hidden, else "Meeting Summary" (which
 *   `summaryToBlocks` hides again, so the layout round-trips).
 */
export function blocksToSummarySections(
  blocks: readonly BlockLike[],
  originalSections?: readonly SummarySection[] | null,
): EditedSummarySection[] {
  const originalFirstTitle = originalSections?.[0]?.title;
  const leadingTitle =
    typeof originalFirstTitle === "string" && MEETING_SUMMARY_TITLE.test(originalFirstTitle)
      ? originalFirstTitle
      : DEFAULT_SUMMARY_SECTION_TITLE;

  const sections: EditedSummarySection[] = [];
  let current: EditedSummarySection | null = null;

  collectLines(blocks, (block, text, depth) => {
    if (block.type === "heading" && depth === 0) {
      if (text) {
        current = { title: text, bullets: [] };
        sections.push(current);
      }
      return;
    }
    if (!text) return;
    if (!current) {
      current = { title: leadingTitle, bullets: [] };
      sections.push(current);
    }
    current.bullets.push(text);
  });

  return sections;
}

/**
 * Inverse of `summaryPointsToBlocks` for the legacy `engagement.aiSummary`
 * path: one section holding every non-empty line (heading text included, so
 * nothing the user typed is lost — the legacy view is a flat bullet list).
 */
export function blocksToLegacySummarySections(blocks: readonly BlockLike[]): EditedSummarySection[] {
  const bullets: string[] = [];
  collectLines(blocks, (_block, text) => {
    if (text) bullets.push(text);
  });
  return [{ title: DEFAULT_SUMMARY_SECTION_TITLE, bullets }];
}
